import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Box,
} from '@mui/material';
import { PostTemplateMaster } from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import {
  DATE_FORMAT,
  INSPECTION_CLASS,
  TEMPLATE_CLASS,
} from '../../../_constants/Const';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { adjustInputYear, isStringInput } from '../../../_utils/utils';
import { API_CODE } from '../../../_constants/apiConst';
import useErrorModal from '../../../hooks/useErrorModal';
import { AppDispatch, RootState } from '../../../stores/store';
import { useDispatch, useSelector } from 'react-redux';
import { createTemplateMaster } from '../../../stores/template/slice';
import { isRejected } from '@reduxjs/toolkit';

type CreateTemplateModalProps = {
  isOpen: boolean;
  handleClose: (isSave: boolean) => void;
};

const defaultTemplate: PostTemplateMaster = {
  templateId: 0,
  inspectionClass: 1,
  templateClass: 1,
  documentNo: '',
  effectiveDate: '',
  templateName: '',
  templateNameJp: '',
  templateName03: '',
  templateName04: '',
  templateName05: '',
  templateName06: '',
  templateName07: '',
  templateName08: '',
  templateName09: '',
  templateName10: '',
  selectedCompanyList: [],
};

const requiredFields: (keyof PostTemplateMaster)[] = [
  'templateName',
  'documentNo',
  'effectiveDate',
];

/** テンプレートの作成ダイアログ */
const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { getApiErrorMessage } = useErrorModal();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  /** エラーメッセージ */
  const [errorMsg, setErrorMsg] = useState<string>('');
  /** 作成中のテンプレート */
  const [newTemplate, setNewTemplate] =
    useState<PostTemplateMaster>(defaultTemplate);
  /** 追加ボタンが非活性か */
  const [isDisabledAddBtn, setIsDisabledAddBtn] = useState<boolean>(false);

  /** 入力チェック */
  useEffect(() => {
    const isAllFilled = requiredFields.every((field) =>
      isStringInput(newTemplate[field]),
    );
    setIsDisabledAddBtn(!isAllFilled);
  }, [newTemplate]);

  /** 入力値の変更 */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof PostTemplateMaster;

    if (typeof newTemplate[fieldName] === 'number') {
      setNewTemplate({ ...newTemplate, [fieldName]: Number(value) });
    } else {
      setNewTemplate({ ...newTemplate, [fieldName]: value });
    }
  };

  /** 日付が変更されたとき */
  const handleDateInputChange = (
    value: string | number | Dayjs | null,
    fieldName: keyof PostTemplateMaster,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }

    setNewTemplate({ ...newTemplate, [fieldName]: date });
  };

  /** キャンセルボタン押下時 */
  const onClickCancel = () => {
    setErrorMsg('');
    setNewTemplate(defaultTemplate);
    handleClose(false);
  };

  /** 追加ボタン押下 */
  const onClickAdd = async () => {
    setErrorMsg('');
    // 連打防止でボタンを一時的に非活性にする
    setIsDisabledAddBtn(true);
    const response = await dispatch(createTemplateMaster(newTemplate));
    setIsDisabledAddBtn(false);
    if (isRejected(response)) {
      setErrorMsg(
        getApiErrorMessage(response.payload, API_CODE.TEMPLATE_MASTER),
      );
      return;
    }
    setNewTemplate(defaultTemplate);
    handleClose(true);
  };

  return (
    <Dialog open={isOpen}>
      <Box display="flex">
        <DialogTitle>{t('LBL_REGISTER')}</DialogTitle>
        <Typography alignSelf="center" color={'error'}>
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('LBL_LANG_EN') })}
          type="text"
          fullWidth
          name="templateName"
          value={newTemplate.templateName}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <TextField
          margin="dense"
          label={t('LBL_INSPECTION_CLASS')}
          select
          fullWidth
          name="inspectionClass"
          value={newTemplate.inspectionClass}
          onChange={handleInputChange}
        >
          {Object.keys(INSPECTION_CLASS).map((key) => (
            <MenuItem key={key} value={key}>
              {t(INSPECTION_CLASS[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_CLASS')}
          select
          fullWidth
          name="templateClass"
          value={newTemplate.templateClass}
          onChange={handleInputChange}
        >
          {Object.keys(TEMPLATE_CLASS).map((key) => (
            <MenuItem key={key} value={key}>
              {t(TEMPLATE_CLASS[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_DOCUMENT_NO')}
          type="text"
          fullWidth
          name="documentNo"
          value={newTemplate.documentNo}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.DOCUMENT_NO,
            },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={t('LBL_EFFECTIVE_DATE')}
            value={
              newTemplate.effectiveDate === ''
                ? null
                : dayjs(newTemplate.effectiveDate)
            }
            onChange={(value) => handleDateInputChange(value, 'effectiveDate')}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'dense',
              },
              field: {
                clearable: true,
                onClear: () => handleDateInputChange('', 'effectiveDate'),
              },
            }}
            format={DATE_FORMAT[userData.dateFormat]}
            views={['year', 'month', 'day']}
            sx={{ bgcolor: 'warning.light' }}
          />
        </LocalizationProvider>
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('LBL_LANG_JA') })}
          type="text"
          fullWidth
          name="templateNameJp"
          value={newTemplate.templateNameJp}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang3') })}
          type="text"
          fullWidth
          name="templateName03"
          value={newTemplate.templateName03}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang4') })}
          type="text"
          fullWidth
          name="templateName04"
          value={newTemplate.templateName04}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang5') })}
          type="text"
          fullWidth
          name="templateName05"
          value={newTemplate.templateName05}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang6') })}
          type="text"
          fullWidth
          name="templateName06"
          value={newTemplate.templateName06}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang7') })}
          type="text"
          fullWidth
          name="templateName07"
          value={newTemplate.templateName07}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang8') })}
          type="text"
          fullWidth
          name="templateName08"
          value={newTemplate.templateName08}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang9') })}
          type="text"
          fullWidth
          name="templateName09"
          value={newTemplate.templateName09}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang10') })}
          type="text"
          fullWidth
          name="templateName10"
          value={newTemplate.templateName10}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickCancel}
          color="inherit"
          variant="contained"
          sx={{ width: '120px', mr: 2, my: 1 }}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          onClick={onClickAdd}
          color="primary"
          variant="contained"
          disabled={isDisabledAddBtn}
          sx={{ width: '120px', mr: 3, my: 1 }}
        >
          {t('BTN_ADD')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;
